import React, { useState } from 'react';
import { JSONTree } from 'react-json-tree';
import { Dropdown } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const EditableFieldMappingTree = ({ data, onChange }) => {
    const [editingPath, setEditingPath] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [showAddMenu, setShowAddMenu] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [newKeyName, setNewKeyName] = useState('');

    const theme = {
        scheme: 'monokai',
        author: 'wimer hazenberg (http://www.monokai.nl)',
        base00: '#272822',
        base01: '#383830',
        base02: '#49483e',
        base03: '#75715e',
        base04: '#a59f85',
        base05: '#f8f8f2',
        base06: '#f5f4f1',
        base07: '#f9f8f5',
        base08: '#f92672',
        base09: '#fd971f',
        base0A: '#f4bf75',
        base0B: '#a6e22e',
        base0C: '#a1efe4',
        base0D: '#66d9ef',
        base0E: '#ae81ff',
        base0F: '#cc6633',
    };

    const getNestedValue = (obj, path) => {
        return path.reduce((current, key) => current[key], obj);
    };

    const setNestedValue = (obj, path, value) => {
        const newData = JSON.parse(JSON.stringify(obj));
        const lastKey = path[path.length - 1];
        const parentPath = path.slice(0, -1);
        const parent = parentPath.reduce((current, key) => current[key], newData);
        
        if (Array.isArray(parent)) {
            parent[parseInt(lastKey)] = value;
        } else {
            parent[lastKey] = value;
        }
        
        return newData;
    };

    const deleteNestedValue = (obj, path) => {
        const newData = JSON.parse(JSON.stringify(obj));
        const lastKey = path[path.length - 1];
        const parentPath = path.slice(0, -1);
        const parent = parentPath.reduce((current, key) => current[key], newData);
        
        if (Array.isArray(parent)) {
            parent.splice(parseInt(lastKey), 1);
        } else {
            delete parent[lastKey];
        }
        
        return newData;
    };

    const addNestedValue = (obj, path, key, value) => {
        const newData = JSON.parse(JSON.stringify(obj));
        const target = path.reduce((current, key) => current[key], newData);
        
        if (Array.isArray(target)) {
            target.push(value);
        } else {
            target[key] = value;
        }
        
        return newData;
    };

    const handleDelete = (path) => {
        const newData = deleteNestedValue(data, path);
        onChange(newData);
    };

    const handleAdd = (path, type, location = 'into') => {
        let newValue;
        switch (type) {
            case 'array':
                newValue = [];
                break;
            case 'object':
                newValue = {};
                break;
            case 'string':
                newValue = '';
                break;
            default:
                newValue = '';
        }
        
        let newData;
        if (location === 'after') {
            // Add after current field - add to parent
            const parentPath = path.slice(0, -1);
            const currentKey = path[path.length - 1];
            newData = addNestedValue(data, parentPath, `new_${type}_${Date.now()}`, newValue);
        } else {
            // Add into current field - add to current
            newData = addNestedValue(data, path, `new_${type}_${Date.now()}`, newValue);
        }
        
        onChange(newData);
        setShowAddMenu(null);
    };

    const handleKeyEdit = (path, oldKey) => {
        setEditingKey(path.join('.'));
        setNewKeyName(oldKey);
    };

    const handleKeySave = (path, oldKey) => {
        if (newKeyName && newKeyName !== oldKey) {
            const newData = JSON.parse(JSON.stringify(data));
            const parentPath = path.slice(0, -1);
            const parent = parentPath.reduce((current, key) => current[key], newData);
            const value = parent[oldKey];
            
            if (Array.isArray(parent)) {
                // For arrays, we can't change keys, only values
                return;
            } else {
                delete parent[oldKey];
                parent[newKeyName] = value;
            }
            
            onChange(newData);
        }
        setEditingKey(null);
        setNewKeyName('');
    };

    const labelRenderer = (keyPath, nodeType, expanded, expandable) => {
        const key = keyPath[0];
        const path = keyPath.slice().reverse();
        const isEditingKey = editingKey === path.join('.');
        
        const items = [
            {
                label: `Insert after ${key}`,
                key: "2",
                children: [
                    {
                        key: "2-1",
                        label: "Array",
                    },
                    {
                        key: "2-2",
                        label: "Object",
                    },
                    {
                        key: "2-3",
                        label: "String",
                    },
                ],
            },
        ];

        if (nodeType === "Array" || nodeType === "Object") {
            items.splice(0, 0, {
                label: `Insert into ${key}`,
                key: "1",
                children: [
                    {
                        key: "1-1",
                        label: "Array",
                    },
                    {
                        key: "1-2",
                        label: "Object",
                    },
                    {
                        key: "1-3",
                        label: "String",
                    },
                ],
            });
        }

        const handleMenuClick = (e) => {
            e.domEvent.stopPropagation();
            const [action, type] = e.key.split('-');
            const datatypes = ["array", "object", "string"];
            const location = action === "1" ? "into" : "after";
            handleAdd(path, datatypes[parseInt(type) - 1], location);
        };
        
        return (
            <div className="json-label-container">
                <div className="edit-action-container">
                    <DeleteOutlined
                        className="anticon-delete"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(path);
                        }}
                    />
                    <Dropdown 
                        menu={{ items, onClick: handleMenuClick }} 
                        trigger={["click"]}
                    >
                        <a
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                        >
                            <PlusOutlined className="anticon-plus" />
                        </a>
                    </Dropdown>
                </div>
                
                {/* Key name - editable */}
                {isEditingKey ? (
                    <input
                        type="text"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        onBlur={() => handleKeySave(path, key)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleKeySave(path, key);
                            }
                            if (e.key === 'Escape') {
                                setEditingKey(null);
                                setNewKeyName('');
                            }
                        }}
                        className="key-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <div className="json-key-name">
                        <span
                            className="key-name"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (path.length > 0) { // Don't allow editing root key
                                    handleKeyEdit(path, key);
                                }
                            }}
                        >
                            {key}:
                        </span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ position: 'relative' }}>
            <style>{`
                .editable-field-mapping-tree .rstcustom__row {
                    position: relative;
                }
                .editable-field-mapping-tree .rstcustom__row:hover {
                    background-color: rgba(24, 144, 255, 0.05);
                }
                .editable-field-mapping-tree .json-label-container {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    width: 100%;
                }
                .editable-field-mapping-tree .edit-action-container {
                    display: inline;
                    opacity: 0;
                    transition: all 0.1s linear;
                    margin-right: 8px;
                }
                .editable-field-mapping-tree .json-label-container:hover .edit-action-container {
                    opacity: 1;
                }
                .editable-field-mapping-tree .anticon-delete {
                    color: #ff4d4f;
                    cursor: pointer;
                    font-size: 12px;
                    padding: 2px;
                    transition: color 0.3s ease;
                }
                .editable-field-mapping-tree .anticon-delete:hover {
                    color: #ff7875;
                }
                .editable-field-mapping-tree .anticon-plus {
                    color: #52c41a;
                    cursor: pointer;
                    font-size: 12px;
                    padding: 2px;
                    transition: color 0.3s ease;
                }
                .editable-field-mapping-tree .anticon-plus:hover {
                    color: #73d13d;
                }
                .editable-field-mapping-tree .json-key-name {
                    position: relative;
                    display: inline;
                    cursor: pointer;
                    padding-left: 10px;
                    flex: 1;
                }
                .editable-field-mapping-tree .key-name {
                    cursor: pointer;
                    transition: color 0.2s ease;
                }
                .editable-field-mapping-tree .key-name:hover {
                    color: #1890ff;
                }
                .editable-field-mapping-tree .key-input {
                    background: transparent;
                    border: 1px solid #1890ff;
                    color: inherit;
                    font-size: inherit;
                    font-family: inherit;
                    padding: 2px 4px;
                    border-radius: 2px;
                    outline: none;
                }
            `}</style>
            <JSONTree
                className="editable-field-mapping-tree"
                theme={{
                    extend: theme,
                    tree: {
                        padding: "20px",
                        borderRadius: "7px",
                    },
                    nestedNodeItemString: ({ style }, keyPath, nodeType, expanded) => ({
                        style: {
                            ...style,
                            paddingLeft: "0.5em",
                            cursor: "default",
                            color: theme.base03,
                        },
                    }),
                }}
                data={data}
                shouldExpandNodeInitially={() => true}
                hideRoot
                labelRenderer={labelRenderer}
            />
        </div>
    );
};

export default EditableFieldMappingTree;